import cn from 'classnames';
import React, { FC, useMemo } from 'react';

import { Bars } from '@.partials';
import { Filter3, FilterData } from '@.ui';
import { AnalyticChartCardInfo } from '@/Components/Partials/Analytics/AnalyticChartCardInfo';
import { AnalyticChartFilterInfo } from '@/Components/Partials/Analytics/AnalyticChartFilterInfo';
import { isNumber } from '@/Lib';
import { analyticsStoreSelectors } from '@/Store';
import { ChartDatasetValues, StatisticBestProduct, StatisticBrand, WhiffTotalsValue } from '@/Types/analytics/chartsTypes';

import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '../FavoriteHeartChart/constants';
import S from './styles.module.scss';

type WhiffCountsPerItemChartProps = {
    data: ChartDatasetValues;
    brands: StatisticBrand[];
    bestProduct?: StatisticBestProduct;
    totalWhiffs: WhiffTotalsValue;
    mini?: boolean;
};

export const WhiffCountsPerItemChart: FC<WhiffCountsPerItemChartProps> = ({ data, bestProduct, brands, totalWhiffs, mini = false }) => {
    const filterWhiffByBrand = analyticsStoreSelectors.use.whiffFilterByBrand();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const bestProductDescription = useMemo(
        () => (
            <>
                <p>{bestProduct?.name}</p>
                <p>most popular per period</p>
            </>
        ),
        [bestProduct]
    );

    const onChangeBrandFilter = (item: FilterData) => {
        const brandId = item.brand_id ? Number(item.brand_id) : null;

        if (!brandId) {
            filterWhiffByBrand(null);
        }

        if (isNumber(brandId) && !isNaN(brandId)) {
            filterWhiffByBrand(brandId);
        }
    };

    const filters = [
        <Filter3
            key={brands.length}
            className={S.info_filters}
            onChange={onChangeBrandFilter}
            fromData={{
                brands,
            }}
            isSaveInQuery={false}
            resetOptionName="All brands"
        >
            <Filter3.Brands visibleLabel={''} visibleName="All brands" />
        </Filter3>,
    ];

    const cardsInfo = useMemo(() => {
        const items: JSX.Element[] = [];

        if (bestProduct) {
            items.push(<AnalyticChartCardInfo key={bestProduct.value} title={bestProduct.value.toString()} description={bestProductDescription} />);
        }

        items.push(<AnalyticChartCardInfo key="total-whiffs" title={totalWhiffs.toString()} description="Total completed operation" />);

        return items;
    }, [bestProduct, bestProductDescription, totalWhiffs]);

    const height = data.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn(S.whiffs_chart, { [S['whiffs_chart-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Spritz Triggered by Users</h3>

            {!mini && <AnalyticChartFilterInfo filters={filters} cardsInfo={cardsInfo} />}

            <Bars data={data} height={height} isHorizontal axisDataName="Product name" isHideFractionalTicks mini={mini} />
        </div>
    );
};
